/**
 * <s-tab-rail> — Tabbed card sliders (homepage "Shop by category").
 *
 * Tabs switch which panel is visible; prev/next buttons and the progress bar always
 * act on the visible panel's track. Native scrolling handles swipe and trackpad.
 *
 * Required child markup:
 *   [role="tab"][aria-controls]       — Tab buttons; the selected one has aria-selected="true".
 *   [role="tabpanel"] [data-rail-track] — One overflow-x track per panel.
 *   [data-rail-prev], [data-rail-next] — Buttons.
 *   [data-rail-progress]               — Thumb inside the progress bar.
 */
class STabRail extends HTMLElement {
  #ac;
  #trackAc;
  #track;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    this.progress = this.querySelector('[data-rail-progress]');

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab), { signal });
      tab.addEventListener('keydown', this.#handleTabKey, { signal });
    });
    this.prev?.addEventListener('click', () => this.#step(-1), { signal });
    this.next?.addEventListener('click', () => this.#step(1), { signal });
    window.addEventListener('resize', this.#update, { passive: true, signal });

    const current = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    if (current) this.select(current, false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#trackAc?.abort();
  }

  select(tab, focus = false) {
    this.tabs.forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
    if (focus) tab.focus();

    const panel = document.getElementById(tab.getAttribute('aria-controls'));
    this.#trackAc?.abort();
    this.#track = panel?.querySelector('[data-rail-track]');
    if (!this.#track) return;
    this.#trackAc = new AbortController();
    this.#track.addEventListener('scroll', this.#update, { passive: true, signal: this.#trackAc.signal });
    this.#track.scrollLeft = 0;
    this.#update();
  }

  #handleTabKey = (event) => {
    const index = this.tabs.indexOf(event.currentTarget);
    const rtl = getComputedStyle(this).direction === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = rtl ? 'ArrowRight' : 'ArrowLeft';
    let target = null;
    if (event.key === forward) target = this.tabs[(index + 1) % this.tabs.length];
    if (event.key === backward) target = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
    if (event.key === 'Home') target = this.tabs[0];
    if (event.key === 'End') target = this.tabs[this.tabs.length - 1];
    if (!target) return;
    event.preventDefault();
    this.select(target, true);
  };

  #step(direction) {
    if (!this.#track) return;
    const rtl = getComputedStyle(this.#track).direction === 'rtl';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce), (max-width: 767px), (hover: none)').matches;
    const distance = Math.max(this.#track.clientWidth * 0.8, 200);
    this.#track.scrollBy({ left: distance * direction * (rtl ? -1 : 1), behavior: reduce ? 'auto' : 'smooth' });
  }

  #update = () => {
    const track = this.#track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    if (this.prev) this.prev.disabled = position <= 2;
    if (this.next) this.next.disabled = position >= max - 2;
    if (this.progress) {
      const visible = track.scrollWidth > 0 ? Math.min(track.clientWidth / track.scrollWidth, 1) : 1;
      const travelled = max > 0 ? position / max : 0;
      this.progress.style.inlineSize = `${visible * 100}%`;
      this.progress.style.insetInlineStart = `${travelled * (1 - visible) * 100}%`;
    }
  };
}

if (!customElements.get('s-tab-rail')) customElements.define('s-tab-rail', STabRail);
